import React, { useEffect, useState } from 'react';
import { Select, Input, message } from 'antd';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { fetchProvinces } from '../../services'; 
import { setSelectedProvince } from '../../redux/slices/locationSlice';
import './selectorProvince.scss';
import { IProvinceSelectorProps } from '../interfaces';

const { Option } = Select;

const SelectorProvince: React.FC<IProvinceSelectorProps> = ({ country, selectedProvince, onProvinceChange }) => {
  const dispatch = useAppDispatch();
  const { provinces } = useAppSelector((state) => state.location);
  const [localSelectedProvince, setLocalSelectedProvince] = useState<string | null>(selectedProvince || '');
  const [isProvinceInputVisible, setIsProvinceInputVisible] = useState(false);

  useEffect(() => {
    setLocalSelectedProvince(selectedProvince || '');
  }, [selectedProvince]);

  useEffect(() => {
    if (!country) {
      setIsProvinceInputVisible(false);
      return;
    }

    const fetchAndSetProvinces = async () => {
      try {
        const data = await dispatch(fetchProvinces({ countryName: country })).unwrap();
        setIsProvinceInputVisible(data.length === 0);

        if (selectedProvince) {
          setLocalSelectedProvince(selectedProvince);
        } else if (data.length === 0) {
          setLocalSelectedProvince('');
        }
      } catch {
        message.error('Failed to fetch provinces.');
      }
    };

    fetchAndSetProvinces();
  }, [country, selectedProvince, dispatch]);


  const handleProvinceChange = (value: string) => {
    if (!isProvinceInputVisible) {
      const selected = provinces.find((province) => province.name === value);
      const provinceName = selected?.name || value;
      setLocalSelectedProvince(provinceName);
      dispatch(setSelectedProvince(provinceName || ''));
      onProvinceChange(provinceName);
    } else {
      setLocalSelectedProvince(value);
      dispatch(setSelectedProvince(value || ''));
      onProvinceChange(value);
    }
  };

  const handleBlur = () => {
    if (isProvinceInputVisible) {
      dispatch(setSelectedProvince(localSelectedProvince || ''));
    }
  };

  return (
    <div className="input-container">
      <label className="input-label" htmlFor="shipping-province">State/Province</label>
      {isProvinceInputVisible ? (
        <Input
          id="shipping-province"
          aria-label="State/Province"
          placeholder="Enter Province"
          value={localSelectedProvince || ''}
          onChange={(e) => handleProvinceChange(e.target.value)}
          onBlur={handleBlur}
          style={{ width: '100%' }}
        />
      ) : (
        <Select
          id="shipping-province"
          aria-label="State/Province"
          showSearch
          placeholder="Select Province"
          disabled={!country || provinces.length === 0}
          onChange={handleProvinceChange}
          value={localSelectedProvince || undefined}
          optionFilterProp="children"
          filterOption={(input, option) =>
            (typeof option?.children === 'string' ? (option.children as string).toLowerCase() : '').includes(input.toLowerCase())
          }
        >
          {provinces.map((province) => (
            <Option key={province.id} value={province.name}>
              {province.name}
            </Option>
          ))}
        </Select>
      )}
    </div>
  );
};

export default SelectorProvince;
